import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  BrowseLevelsLayoutContext,
  getLevelSurfaceKey,
  type LevelSurfacePart,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import { type ReactNode, useMemo } from "react";
import { LevelSlidePages } from "#level-slide-pages";

/**
 * Renders a level surface's slide lessons as small pages on the tile of the card each one plays
 * next to (see `buildLevelSurfaceSlides`). `undefined` while the surface's slides are hidden or it
 * has none, so its grid lays no overlay over any tile.
 */
export function useLevelSurfaceSlidePages({
  className,
  levelIndex,
  part,
}: {
  /** Places the pages row over a tile. */
  className: string;
  levelIndex: number;
  part: LevelSurfacePart;
}): ((character: string) => ReactNode) | undefined {
  const surfaceKey = getLevelSurfaceKey(levelIndex, part);
  const [surfaceSlides, visible] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (context) => context.slidesBySurface.get(surfaceKey),
    (context) => context.showSlideLessons,
  );
  return useMemo(() => {
    if (!visible || surfaceSlides === undefined) {
      return undefined;
    }
    return (character: string) => {
      const slides = surfaceSlides.get(character);
      return slides === undefined ? null : (
        <LevelSlidePages className={className} slides={slides} />
      );
    };
  }, [className, surfaceSlides, visible]);
}
